import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isEmpty } from "lodash-es";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useApi } from "../../shared/api/index";
import { useNotification } from "~hooks/useNotification";

export const useFetchStandingConfiguration = () => {
  const { fetchData } = useApi();
  const { organizationId, sport, standingConfigurationId } = useParams();

  return useQuery({
    queryKey: ["standingsConfigurations", organizationId, standingConfigurationId],
    queryFn: async () => {
      const result = await fetchData({
        url: `${sport}/o/${organizationId}/standings/configurations/${standingConfigurationId}`,
      });
      return result?.data?.[0];
    },
    enabled: !!standingConfigurationId,
  });
};

export const useFetchStandingsConfigurations = (params = {}) => {
  const { fetchData } = useApi();
  const { organizationId, sport } = useParams();
  let URL = `${sport}/o/${organizationId}/standings/configurations`;
  if (!isEmpty(params)) {
    URL += `?${new URLSearchParams(params)?.toString()}`;
  }

  return useQuery({
    queryKey: ["standingsConfigurations", organizationId, JSON.stringify(params)],
    queryFn: async () => {
      const result = await fetchData({
        url: URL,
      });
      return result?.data || [];
    },
  });
};

export const useCreateStandingsConfiguration = () => {
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (data) => {
      await mutateData({
        url: `${sport}/o/${organizationId}/standings/configurations`,
        method: "POST",
        data,
      });
    },
    onSuccess: () => {
      notification.success({
        message: formatMessage({ id: "standings.configuration.add.success" }),
        placement: "bottomRight",
      });
      queryClient.invalidateQueries({
        queryKey: ["standingsConfigurations", sport, organizationId],
      });
    },
  });
};

export const useUpdateStandingConfiguration = () => {
  const { sport, organizationId, standingConfigurationId } = useParams();
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (data) => {
      await mutateData({
        url: `${sport}/o/${organizationId}/standings/configurations/${standingConfigurationId}`,
        method: "PUT",
        data,
      });
    },
    onSuccess: () => {
      notification.success({
        message: formatMessage({ id: "standings.configuration.update.success" }),
        placement: "bottomRight",
      });
      queryClient.invalidateQueries({
        queryKey: ["standingsConfigurations", sport, organizationId],
      });
    },
  });
};
